import {
  build_case_fact_items,
  normalize_label,
  normalize_text,
  question_key,
  section_display_name,
  to_number,
} from './request_portal_helpers';

function answer_record(key, value) {
  return { key: String(key || ''), value };
}

function is_file_value(value) {
  return typeof File !== 'undefined' && value instanceof File;
}

function serialized_answer_value(value) {
  if (is_file_value(value)) return value.name;
  return Array.isArray(value) ? value.join(', ') : value;
}

function question_answer_text(question = {}) {
  const raw_answer = question?.answer;
  if (Array.isArray(raw_answer)) return raw_answer.join(', ');
  if (typeof raw_answer === 'object' && raw_answer !== null) {
    return normalize_text(raw_answer?.file_name || raw_answer?.original_url || '');
  }
  return normalize_text(raw_answer);
}

function runtime_question_payload(question = {}) {
  return {
    form_id: to_number(question?.form_id),
    form_question_key: normalize_text(question?.form_question_key),
    section_question_id: to_number(question?.section_question_id),
    question_id: to_number(question?.id || question?.question_id),
    question_text: normalize_text(question?.question_text),
  };
}

function submitted_answer_payload(question = {}, raw_value) {
  const is_file = is_file_value(raw_value);
  return {
    form_id: to_number(question?.form_id),
    form_question_key: question?.form_question_key || question_key(question),
    section_question_id: to_number(question?.section_question_id),
    question_id: to_number(question?.question_id || question?.id),
    question_text: question.question_text,
    question_type: section_question_type(question),
    answer_text: normalize_text(serialized_answer_value(raw_value)),
    file: is_file ? raw_value : null,
  };
}

function section_question_type(question = {}) {
  return normalize_text(
    question?.question_type?.question_type
    || question?.question_type?.type
    || question?.question_type
  ).toLowerCase();
}

function section_question_options(question = {}) {
  return Array.isArray(question?.multi_select_choices || question?.options)
    ? (question?.multi_select_choices || question?.options)
    : [];
}

function case_fact_source(question = {}, section = {}, form = {}, application_form = {}) {
  return {
    ...runtime_question_payload(question),
    application_id: to_number(application_form?.application_id),
    question_type: section_question_type(question),
    options: section_question_options(question),
    answer_text: question_answer_text(question),
    raw_answer: question?.answer,
    section_name: section_display_name(section, form),
    application_form_id: to_number(application_form?.application_form_id),
  };
}

function section_answer_record(section = {}, section_index = 0, application_form = {}) {
  const form = application_form?.form || {};
  const facts = build_case_fact_items(
    (section?.questions || []).map((question) => case_fact_source(question, section, form, application_form)),
    { include_unanswered: true, limit: Number.POSITIVE_INFINITY }
  );

  return {
    id: section?.id || section?.section_id || section_index,
    section_key: `${application_form?.application_form_id || application_form?.id || 'form'}-${section?.id || section?.section_id || section_index}`,
    application_form_id: to_number(application_form?.application_form_id),
    name: section_display_name(section, form),
    facts,
  };
}

function answer_lookup_maps(submitted_answers = []) {
  const maps = {
    by_form_question_key: new Map(),
    by_section_question_id: new Map(),
    by_question_id: new Map(),
    by_question_text: new Map(),
  };

  submitted_answers.forEach((answer) => {
    const section_question_id = to_number(answer?.section_question_id);
    const question_id = to_number(answer?.question_id);
    const question_text = normalize_label(answer?.question_text);
    const form_question_key = normalize_text(answer?.form_question_key);

    if (form_question_key) maps.by_form_question_key.set(form_question_key, answer);
    if (section_question_id) maps.by_section_question_id.set(section_question_id, answer);
    if (question_id) maps.by_question_id.set(question_id, answer);
    if (question_text) maps.by_question_text.set(question_text, answer);
  });

  return maps;
}

function matching_submitted_answer(question = {}, maps = {}) {
  return maps.by_form_question_key.get(normalize_text(question?.form_question_key))
    || maps.by_section_question_id.get(question.section_question_id)
    || (question.question_id
      ? maps.by_question_id.get(question.question_id)
      : maps.by_question_text.get(normalize_label(question.question_text)));
}

function answer_submission_payload(question = {}, maps = {}) {
  const answer = matching_submitted_answer(question, maps);
  const is_file = is_file_value(answer?.file);
  const answer_text = is_file ? answer.file.name : answer?.answer_text;

  if (!question.section_question_id || !normalize_text(answer_text)) return null;

  return {
    section_question_id: question.section_question_id,
    question_id: question.question_id,
    answer_text,
    ...(is_file ? { file: answer.file } : {}),
  };
}

const ENTITY_NAME_LABEL_TOKENS = [
  'customer name',
  'client name',
  'company name',
  'business name',
  'organisation name',
  'organization name',
  'requester name',
  'requestor name',
  'applicant name',
  'claimant name',
  'entity name',
  'name of customer',
  'name of client',
  'name of company',
  'name of business',
];

function is_choice_answer(answer = {}) {
  const question_type = normalize_text(answer?.question_type).replace(/\s+/g, '_').toLowerCase();
  return question_type.includes('select')
    || question_type.includes('radio')
    || question_type.includes('checkbox');
}

function is_identity_answer(answer = {}) {
  if (is_choice_answer(answer)) return false;
  const question_text = normalize_label(answer?.question_text);
  return ENTITY_NAME_LABEL_TOKENS.some((token) => question_text.includes(token));
}

function generated_case_entity_name(case_type = '') {
  const label = normalize_text(case_type) || 'Requestor Case';
  return `${label} ${Date.now()}`;
}

export default {
  answer_value_by_key(key) {
    return (this.answer_values || []).find((record) => record.key === String(key || ''))?.value;
  },
  set_answer_value_by_key(key, value) {
    const next_record = answer_record(key, value);
    if (!next_record.key) return;

    const current_values = Array.isArray(this.answer_values) ? this.answer_values : [];
    const has_record = current_values.some((record) => record.key === next_record.key);
    const next_values = current_values.map((record) =>
      record.key === next_record.key ? next_record : record
    );

    this.answer_values = has_record ? next_values : [...next_values, next_record];
  },
  get_answer_value(question = {}) {
    return this.answer_value_by_key(question_key(question)) || '';
  },
  get_multi_answer_value(question = {}) {
    const current_value = this.answer_value_by_key(question_key(question));
    return Array.isArray(current_value) ? current_value : [];
  },
  update_answer_value(question, value) {
    this.set_answer_value_by_key(question_key(question), value);
    this.remove_validation_error(question);
  },
  update_multi_answer_value(question, selected_options) {
    this.set_answer_value_by_key(
      question_key(question),
      Array.from(selected_options || []).map((option) => option.value)
    );
    this.remove_validation_error(question);
  },
  update_checkbox_answer_value(question, option_value, is_checked) {
    const current_values = this.get_multi_answer_value(question);
    const next_values = is_checked
      ? [...new Set([...current_values, option_value])]
      : current_values.filter((value) => value !== option_value);

    this.set_answer_value_by_key(question_key(question), next_values);
    this.remove_validation_error(question);
  },
  update_file_answer_value(question, files) {
    const [selected_file] = Array.from(files || []);
    this.set_answer_value_by_key(question_key(question), selected_file || null);
    this.remove_validation_error(question);
  },
  remove_validation_error(question) {
    const key = question_key(question);
    this.validation_errors = this.validation_errors.filter((error) => error.key !== key);
    if (!this.validation_errors.length) this.modal_error = '';
  },
  validation_error_by_key(key) {
    return (this.validation_errors || []).find((record) => record.key === String(key || ''))?.message || '';
  },
  has_question_error(question = {}) {
    return Boolean(this.validation_error_by_key(question_key(question)));
  },
  question_error_message(question = {}) {
    return this.validation_error_by_key(question_key(question));
  },
  is_answer_empty(question, answer) {
    if (is_file_value(answer)) return false;
    if (Array.isArray(answer)) return !answer.some((entry) => normalize_text(entry));
    if (this.is_file_question(question)) return !answer;
    return !normalize_text(answer);
  },
  validate_dynamic_form() {
    const visible_questions = this.section_cards.flatMap((section) => section.questions || []);
    const has_any_answer = visible_questions.some((question) => {
      const answer = this.answer_value_by_key(question_key(question));
      return !this.is_answer_empty(question, answer);
    });

    if (visible_questions.length && !has_any_answer) {
      this.validation_errors = [];
      this.modal_error = 'Complete the form before opening the case.';
      this.scroll_new_case_feedback_into_view();
      return false;
    }

    const next_errors = visible_questions
      .filter((question) => this.is_question_required(question))
      .map((question) => {
        const answer = this.answer_value_by_key(question_key(question));
        if (!this.is_answer_empty(question, answer)) return null;
        return { key: question_key(question), message: `"${question.question_text}" is required.` };
      })
      .filter(Boolean);

    this.validation_errors = next_errors;
    this.modal_error = next_errors.length ? 'Please complete all required fields before submitting.' : '';
    if (next_errors.length) {
      this.scroll_new_case_feedback_into_view();
    }
    return !next_errors.length;
  },
  build_submission_answers() {
    return this.ordered_sections.flatMap((main_section) =>
      (main_section.sub_sections || []).flatMap((sub_section) =>
        (sub_section.questions || []).map((question) =>
          submitted_answer_payload(question, this.answer_value_by_key(question_key(question)))
        )
      )
    );
  },
  flatten_application_form_questions(application_form = {}) {
    return (application_form?.form?.sections || []).flatMap((section) =>
      (section?.questions || []).map(runtime_question_payload)
    );
  },
  flatten_application_form_answers(application_form = {}) {
    return (application_form?.form?.sections || []).flatMap((section) =>
      (section?.questions || []).map((question) => ({
        ...runtime_question_payload(question),
        answer_text: question_answer_text(question),
        raw_answer: question?.answer,
      }))
    );
  },
  flatten_application_form_answer_sections(application_form = {}) {
    return (application_form?.form?.sections || [])
      .map((section, section_index) => section_answer_record(section, section_index, application_form))
      .filter((section) => section.facts.length);
  },
  build_answer_submission_payload(runtime_questions = [], submitted_answers = []) {
    const maps = answer_lookup_maps(submitted_answers);
    return runtime_questions
      .map((question) => answer_submission_payload(question, maps))
      .filter(Boolean);
  },
  application_form_for_form_id(application_forms = [], form_id = null) {
    return application_forms.find((application_form) =>
      Number(application_form?.form?.id || 0) === Number(form_id)
    ) || null;
  },
  select_preferred_application_form(application_forms = []) {
    return application_forms.find((application_form) =>
      Number(application_form?.form?.id || 0) === Number(this.selected_form_id)
    ) || application_forms[0] || null;
  },
  build_case_entity_name(answers = []) {
    const case_type = normalize_text(this.selected_case_type?.title);
    const identity_answer = answers.find((answer) =>
      normalize_text(answer?.answer_text) && is_identity_answer(answer)
    );
    return normalize_text(identity_answer?.answer_text)
      || generated_case_entity_name(case_type);
  },
};
